import { StateDataContext } from 'containers/Common/StateDataContext';
import { EditLimitationModel } from 'containers/Limitations/EditLimitationModel';
import { Order } from 'core/order/Order';
import { useCallback, useContext, useEffect, useRef } from 'react';
import i18n from 'i18n';
import { Limitation, LimitationData } from 'core/limitation/Limitation';
import { getLimitationOp } from 'utils/LimitationUtil';
import { OPERATES } from 'enum/Operate';
import { RtbCampaignContext } from './LimitationManagementContext';
import { DefaultAsyncSourceSpanModel } from 'components/AsyncSourceSpan/AsyncSourceSpanModel';

export type ModifyLimitationStateContentModel = {
  title: string;
  order: Order;
  limitationModel: EditLimitationModel;
  supportAgeGenderLimitation: boolean;
};

export const useAddLimitationStateContentModel = (
  order: Order,
  campaigns: RtbCampaignContext[],
  limitationModel: EditLimitationModel,
  supportAgeGenderLimitation: boolean
) => {
  const { setData } = useContext(StateDataContext);
  const eventHandlerRef = useRef<number>();

  useEffect(() => {
    if (eventHandlerRef.current) {
      limitationModel.event.remove(eventHandlerRef.current);
    }
    // limitation 有變動就更新 context data
    let handler = limitationModel.event.add((model) => {
      setData({
        campaignIds: campaigns.map((campaign) => campaign.id),
        limitations: model.limitationValue
      });
    });
    eventHandlerRef.current = handler;
    return () => {
      limitationModel.event.remove(handler);
    };
  }, [limitationModel, campaigns, setData]);

  return {
    title: i18n.t('modifyLimitationState.titles.add'),
    order,
    limitationModel,
    supportAgeGenderLimitation
  };
};

export const useRemoveLimitationStateContentModel = (
  order: Order,
  campaigns: RtbCampaignContext[],
  limitationModel: EditLimitationModel
) => {
  const getCommonLimitations = useCallback(
    (campaigns: { limitations: LimitationData }[]): Limitation[] => {
      // 拉平所有 limitation
      const allLimitations: Limitation[] = campaigns.reduce<Limitation[]>(
        (acc, campaign) =>
          acc.concat(Object.values(campaign.limitations).flat()),
        []
      );

      // 產生 key: op-type-value
      const makeKey = (limitation: Limitation) => {
        // value 可能是 array 或 string/number
        let valueStr = '';
        if (Array.isArray(limitation.value)) {
          valueStr = limitation.value
            .map((v) => v.value)
            .sort()
            .join(',');
        } else {
          valueStr = String(limitation.value);
        }
        return `${limitation.op ?? ''}-${limitation.type}-${valueStr}`;
      };

      // 計算每個 limitation 出現次數
      const limitationCount: Record<
        string,
        { count: number; limitation: Limitation }
      > = {};
      allLimitations.forEach((limitation) => {
        const key = makeKey(limitation);
        const limitationCountItem = limitationCount[key] || {
          count: 0,
          limitation
        };
        limitationCountItem.count += 1;
        limitationCount[key] = limitationCountItem;
      });

      // 只取出現次數等於 campaigns.length 的
      return Object.values(limitationCount)
        .filter((item) => item.count === campaigns.length)
        .map((item) => item.limitation);
    },
    []
  );

  useEffect(() => {
    limitationModel.limitationValue.preferred =
      limitationModel.limitationValue.preferred.filter(
        (ta) => !['age_min', 'age_max'].includes(ta.type)
      );
    limitationModel.limitationValue.nonPreferred =
      limitationModel.limitationValue.nonPreferred.filter(
        (ta) => !['age_min', 'age_max'].includes(ta.type)
      );

    // 取得所有 campaign 的共同 limitation
    const commonLimitations = getCommonLimitations(campaigns);
    const limitationInventorySettings = limitationModel.limitationSetting;
    // 只保留與共同 limitation 相符的設定
    const commonSettings = limitationInventorySettings.filter(
      (setting) =>
        setting.name === 'default' ||
        !!commonLimitations.find((lim) => lim.type === setting.name)
    );
    commonSettings.forEach((setting) => {
      // 限制支援的 operation 只能是共有 limitation 有設定的 operation
      // ex: 所有活動共有的國家 limitation 只有出現在 include, 那麼國家選項只能在 include 出現
      setting.supportOperates = commonLimitations
        .filter((limitation) => limitation.type === setting.name)
        .map((limitation) => getLimitationOp(limitation.op! || 'other'));
      // 讓選項不會被 catch，因為不同 operation 會有不同的選項，
      // 這樣可以避免在不同 operation 的選單中出現不符合的選項
      setting.doNotCatchOptions = true;
      setting.ignoreValidateOption = true;
      // 讓選取的 limitation 不能被更改 operation
      // ex: 國家只能設定在 include, 不能讓使用者變更到 exclude
      setting.cannotChangeOperation = true;
      setting.postCommentPropsGetter = () => {
        return {
          model: new DefaultAsyncSourceSpanModel(() =>
            i18n.t('modifyLimitationState.hints.remove')
          )
        };
      };
      // 讓使用者可以切換 operation 後，拿到符合 operation 的共有 limitation 選項
      setting.cb = (operate?: OPERATES) => {
        return commonLimitations
          .filter(
            (limitation) =>
              limitation.type === setting.name &&
              getLimitationOp(limitation.op!) === operate
          )
          .flatMap((limitation) => limitation.value as SelectOptions[]);
      };
    });
    limitationModel.setLimitationSetting(commonSettings);
  }, [limitationModel, campaigns, getCommonLimitations]);

  useAddLimitationStateContentModel(order, campaigns, limitationModel, false);

  return {
    title: i18n.t('modifyLimitationState.titles.remove'),
    order,
    limitationModel,
    supportAgeGenderLimitation: false
  };
};
